import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { Text, Icon } from '../../uiold';
import { themeColors } from '../../uiold/Constants';

import { useBuilderState } from '../BuilderContext';
import DragHandle from './DragHandle';

import { FormElementDefinition } from '../../../types/shared/EvaluationForm';
import { FormElementProps } from '../types';

type Props = FormElementProps<FormElementDefinition> & {
  children: React.ReactNode;
};

function BuilderViewField(props: Props) {
  let { definition, sortIndex, style, children, last } = props;
  let { activeFieldId, setActiveField, update } = useBuilderState();
  const [isHovering, setIsHovering] = useState(false);

  let isActive = definition.id === activeFieldId;

  let onDeletePress = useCallback(() => {
    update((draft) => {
      let previousField = draft.fields[sortIndex - 1];
      draft.fields.splice(sortIndex, 1);
      let nextField = draft.fields[sortIndex];
      setActiveField(
        // Focus to previous field if exist
        previousField
          ? {
              activeFieldId: previousField.id,
              activeFieldIndex: sortIndex - 1,
            } // Else, focus on the next one
          : nextField
          ? {
              activeFieldId: nextField.id,
              // The next field index will be the same since we deleted the previous.
              activeFieldIndex: sortIndex,
            } // When no more field exist, clear focus.
          : {
              activeFieldId: null,
              activeFieldIndex: null,
            },
      );
    });
  }, [sortIndex, update, setActiveField]);

  let showHandle = isActive || isHovering;

  return useMemo(
    () => (
      <View style={style}>
        <TouchableOpacity
          disabled={isActive}
          activeOpacity={1}
          style={styles.root}
          onPress={() =>
            setActiveField({
              activeFieldId: definition.id,
              activeFieldIndex: sortIndex,
            })
          }
          nativeID={definition.id}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <DragHandle showHandle={showHandle} />
          {children}
          <View style={styles.footer}>
            {isActive && (
              <TouchableOpacity onPress={onDeletePress}>
                {/* Text is used to add "title" attribute */}
                <Text title={t('Delete')}>
                  <Icon name="remove" size="M" />
                </Text>
              </TouchableOpacity>
            )}
          </View>
          {isActive && <View style={styles.activeIndicator} />}
        </TouchableOpacity>
        {!last && <View style={styles.spacing} />}
      </View>
    ),
    [
      definition.id,
      isActive,
      last,
      style,
      showHandle,
      children,
      onDeletePress,
      setActiveField,
      sortIndex,
    ],
  );
}

let styles = StyleSheet.create({
  root: {
    paddingHorizontal: 16,
    backgroundColor: themeColors.white,
    borderWidth: 1,
    borderColor: themeColors.borderGrey,
    borderRadius: 5,
    overflow: 'hidden',
  },
  activeIndicator: {
    position: 'absolute',
    width: 6,
    top: 0,
    bottom: 0,
    right: 0,
    backgroundColor: themeColors.blue,
  },
  spacing: {
    height: 8,
  },
  footer: {
    height: 48,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: themeColors.borderGrey,
  },
});

export default BuilderViewField;
